import { possuiConsentimentoAtivo } from '@shared/cookie-consent/utils/consentimentoAtual';
import {
  EVENTO_PAGEVIEW_VIRTUAL,
  isEventoMarketing,
  obterEventoMeta,
} from '@shared/marketing-tracking/utils/eventosMarketing';
import type { RouteLocationNormalized } from 'vue-router';
import cookieService from '@/services/CookieService';
import queryStringService from '@/services/QueryStringService';

const PARAMETROS_ATRIBUICAO = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;

type ParametroAtribuicao = (typeof PARAMETROS_ATRIBUICAO)[number];

export type ParametrosAtribuicao = Partial<Record<ParametroAtribuicao | 'fbp' | 'fbc', string>>;

export function gerarEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    return `${array[0].toString(36)}-${array[1].toString(36)}`;
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * A query da entrada fica persistida em storage (App.vue faz o merge), o que mantém a origem do
 * clique no anúncio enquanto o usuário navega pelo portal.
 */
export function obterParametrosAtribuicao(): ParametrosAtribuicao {
  const query = { ...queryStringService.getFromStorage(), ...queryStringService.getAsObject() };
  const parametros: ParametrosAtribuicao = {};

  PARAMETROS_ATRIBUICAO.forEach(nome => {
    if (query[nome]) parametros[nome] = query[nome];
  });

  const fbp = cookieService.getCookie('_fbp');
  const fbc = cookieService.getCookie('_fbc');
  if (fbp) parametros.fbp = fbp;
  if (fbc) parametros.fbc = fbc;

  return parametros;
}

/**
 * O GTM mantém as chaves de um push para o outro. `meta_evento` vai sempre, `undefined` quando o
 * evento não é da Meta, para não herdar o valor do evento anterior e disparar a tag da Meta de novo.
 */
function empilhar(nome: string, eventId: string, parametros: Record<string, string> = {}): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: nome,
    event_id: eventId,
    meta_evento: obterEventoMeta(nome),
    ambiente: 'portal',
    ...parametros,
    ...obterParametrosAtribuicao(),
  });
}

/**
 * Nenhum parâmetro do payload interno (valores, credor, ids de dívida/proposta) é repassado:
 * dado financeiro só vai às plataformas com aval do Jurídico/DPO.
 */
export function publicarEventoMarketing(nome: string, eventId: string): void {
  if (!isEventoMarketing(nome) || !possuiConsentimentoAtivo()) return;

  empilhar(nome, eventId);
}

/**
 * O portal é SPA: sem isto, as plataformas só veem o primeiro carregamento. O caminho enviado é o
 * padrão da rota (ex.: `/divida/:api/:dividaId/checkout/:ofertaId?`), nunca a URL real, que traz
 * ids e dados da dívida.
 */
export function publicarPageviewVirtual(rota: Pick<RouteLocationNormalized, 'name' | 'matched'>): void {
  if (!possuiConsentimentoAtivo()) return;

  const padraoRota = rota.matched[rota.matched.length - 1]?.path;
  if (!padraoRota) return;

  empilhar(EVENTO_PAGEVIEW_VIRTUAL, gerarEventId(), {
    pagina: String(rota.name ?? ''),
    page_path: padraoRota,
  });
}
